import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "./api/axios";
import { isAdverseEventsComplete } from "./utils/formCompletion";
import "./styles/global.css";
import "./styles/FormComponents.css";
import "./styles/FormAE.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toDateOnlyValue, parseDateOnly } from "./utils/datetime";
import { fallbackCompletedByDesignation as getDesignation } from "./utils/completedByDesignation";
import FormNavBar from "./components/FormNavBar";
import { usePatient } from "./context/PatientContext";
import { useFormProgress } from "./context/FormProgressContext";
import { stripDigits } from "./utils/personName";
import { sanitizeMaternalUid } from "./utils/maternalUid";
import {
  Home,
  ClipboardList,
  AlertCircle,
  ListChecks,
  UserRound,
  Plus,
  Trash2,
  Maximize2,
  X,
  ScanSearch,
  FileText,
} from "lucide-react";

const AE_SCALE_PDF = "/documents/PORTAL_Trial_v2_AdvEvents.pdf";

const GRADES = ["1", "2", "3", "4", "5"];

// "Grade 2: Moderate ..." -> "2": staff paste the INC scale text and leave
// the Grade box blank (PI 2026-11-03). Same rule as ae_grade_from_text.
function gradeFromText(text) {
  const m = String(text || "").match(/^\s*grade\s*([1-5])\b/i);
  return m ? m[1] : "";
}

function emptyToNull(v) {
  if (v === "" || v === undefined || v === null) return null;
  return v;
}

function dateOnly(v) {
  if (!v) return "";
  const m = String(v).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : String(v);
}

function blankEvent() {
  return {
    description: "",
    definition_no: "",
    start_date: "",
    end_date: "",
    severity_desc: "",
    grade: "",
    converted_to_sae: "",
  };
}

function BLANK(enrollmentId = "") {
  return {
    enrollment_id: enrollmentId,
    mother_name: "",
    baby_uid: "",
    maternal_uid: "",
    has_adverse_event: "",
    events: [],
    completed_by: "",
    designation: "",
    completion_date: "",
  };
}

function mapApiToForm(row) {
  const has =
    row.has_adverse_event === true || row.has_adverse_event === "yes" || row.has_adverse_event === "Yes"
      ? "Yes"
      : row.has_adverse_event === false || row.has_adverse_event === "no" || row.has_adverse_event === "No"
        ? "No"
        : "";
  const events = Array.isArray(row.events)
    ? row.events.map((e) => ({
        description: e?.description || "",
        definition_no: e?.definition_no || "",
        start_date: dateOnly(e?.start_date),
        end_date: dateOnly(e?.end_date),
        severity_desc: e?.severity_desc || "",
        grade: e?.grade != null && e?.grade !== "" ? String(e.grade) : "",
        // page only (not sent): the Grade box follows the severity text.
        // A saved blank grade is not filled on opening (prospective, PI
        // 2026-11-03); editing the text fills it.
        grade_auto: e?.grade != null && e?.grade !== "" && String(e.grade) === gradeFromText(e?.severity_desc),
        converted_to_sae: e?.converted_to_sae || "",
        row_id: e?.row_id || "",
      }))
    : [];
  return {
    ...BLANK(row.enrollment_id || ""),
    enrollment_id: row.enrollment_id || "",
    mother_name: row.mother_name || "",
    baby_uid: row.baby_uid || "",
    maternal_uid: row.maternal_uid || "",
    has_adverse_event: has,
    events,
    completed_by: row.completed_by || "",
    designation: row.designation || "",
    completion_date: dateOnly(row.completion_date),
  };
}

// base = the rows as this page last loaded / saved them: the server merges
// row by row, so a co-worker's save in between is kept (list_merge.py).
function buildPayload(data, base = null) {
  const hasYes = data.has_adverse_event === "Yes";
  const events = hasYes
    ? (data.events || []).map((e) => ({
        description: emptyToNull(e.description),
        definition_no: emptyToNull(e.definition_no),
        start_date: emptyToNull(e.start_date),
        end_date: emptyToNull(e.end_date),
        severity_desc: emptyToNull(e.severity_desc),
        grade: emptyToNull(e.grade),
        converted_to_sae: emptyToNull(e.converted_to_sae),
        row_id: emptyToNull(e.row_id),
      }))
    : [];
  return {
    enrollment_id: data.enrollment_id,
    mother_name: emptyToNull(data.mother_name),
    baby_uid: emptyToNull(data.baby_uid),
    maternal_uid: emptyToNull(data.maternal_uid),
    has_adverse_event:
      data.has_adverse_event === "Yes"
        ? true
        : data.has_adverse_event === "No"
          ? false
          : null,
    events,
    base_events: base,
    completed_by: emptyToNull(data.completed_by),
    designation: emptyToNull(data.designation),
    completion_date: emptyToNull(data.completion_date),
  };
}

function DateField({ value, onChange, disabled = false, className = "ae-input" }) {
  return (
    <div className="ae-date-wrap">
      <DatePicker
        selected={value ? parseDateOnly(value) : null}
        onChange={(date) => onChange(date ? toDateOnlyValue(date) : "")}
        dateFormat="dd/MM/yyyy"
        placeholderText="dd/mm/yyyy"
        className={className}
        disabled={disabled}
        portalId="root"
        popperPlacement="bottom-start"
        popperClassName="ae-datepicker-popper"
      />
    </div>
  );
}

function YesNo({ value, onChange, small = false }) {
  return (
    <div className={`ae-yn${small ? " sm" : ""}`}>
      {["Yes", "No"].map((opt) => (
        <button
          key={opt}
          type="button"
          className={`${opt === "Yes" ? "yes" : "no"}${value === opt ? " active" : ""}`}
          onClick={() => onChange(opt)}
        >
          {opt.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

/** Compact cell editor + “View full” modal for long text. */
function ExpandableText({
  value,
  onChange,
  placeholder,
  title = "Full text",
  rows = 2,
}) {
  const [open, setOpen] = useState(false);
  const long = (value || "").length > 80 || (value || "").includes("\n");

  return (
    <div className="ae-expand-cell">
      <textarea
        className="ae-cell-ta"
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        title={value || placeholder}
      />
      <button
        type="button"
        className={`ae-expand-btn${long ? " hot" : ""}`}
        title="View / edit full text"
        onClick={() => setOpen(true)}
      >
        <Maximize2 size={13} />
      </button>

      {open && (
        <div className="ae-modal-backdrop" onClick={() => setOpen(false)}>
          <div
            className="ae-modal"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ae-modal-head">
              <h4>{title}</h4>
              <button type="button" className="ae-modal-close" onClick={() => setOpen(false)}>
                <X size={16} />
              </button>
            </div>
            <textarea
              className="ae-modal-ta"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              autoFocus
            />
            <div className="ae-modal-foot">
              <span className="ae-modal-hint">{(value || "").length} characters</span>
              <button type="button" className="ae-modal-done" onClick={() => setOpen(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionCard({ icon: Icon, num, title, children }) {
  return (
    <section className="ae-card">
      <div className="ae-card-header">
        {Icon && <Icon size={18} className="ae-sec-icon" />}
        {num != null && <span className="ae-sec-num">{num}</span>}
        <h3>{title}</h3>
      </div>
      <div className="ae-card-body">{children}</div>
    </section>
  );
}

export default function AdverseEventsForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const { enrollmentId: routeId } = useParams();
  const { patientData } = usePatient();
  const { markFormCompleted, unmarkFormCompleted } = useFormProgress();
  // Green tick = key items + sign-off (utils/formCompletion), checked against
  // the stored record on load and after each save — not "a save happened".
  const syncTick = (data) => {
    if (isAdverseEventsComplete(data)) markFormCompleted("adverse_events");
    else unmarkFormCompleted("adverse_events");
  };

  const [formData, setFormData] = useState(() => BLANK());
  const baseEventsRef = useRef(null);   // rows as last loaded / saved (null = not loaded yet)
  // SAE reports that are linked to one of these AEs (v1.1, PI 2026-09-30):
  // shown next to the event with an Apply for "Converted to SAE" = Yes.
  const [linkedSaes, setLinkedSaes] = useState([]);
  useEffect(() => {
    const eid = formData.enrollment_id;
    if (!eid) return undefined;
    let cancelled = false;
    api.get(`/sae-report/${encodeURIComponent(eid)}`)
      .then((res) => {
        if (cancelled) return;
        setLinkedSaes((Array.isArray(res.data) ? res.data : []).filter((r) => r && r.linked_ae));
      })
      .catch(() => { if (!cancelled) setLinkedSaes([]); });
    return () => { cancelled = true; };
  }, [formData.enrollment_id]);
  const saeLinkedTo = (row) => linkedSaes.find((r) =>
    (r.linked_ae.description || r.linked_ae.definition_no || "") === (row.description || row.definition_no || "")
    && (r.linked_ae.start_date || "") === (dateOnly(row.start_date) || ""));
  const [isSaved, setIsSaved] = useState(false);
  const [aeLoaded, setAeLoaded] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [assessors, setAssessors] = useState([]);
  const [siteName, setSiteName] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanMessage, setScanMessage] = useState("");
  const [justAddedRows, setJustAddedRows] = useState(() => new Set());
  // Advisory-only: a severe Form F (Cranial USG) finding Form H hasn't
  // caught up with yet. Never auto-added as a candidate — see the backend's
  // detect_pending_cranial_usg_findings docstring.
  const [pendingCranialUsgFindings, setPendingCranialUsgFindings] = useState([]);

  const set = (field, value) => {
    setIsSaved(false);
    setFormData((p) => ({ ...p, [field]: value }));
  };

  // A baby who died: a draft Death row, saved only if staff save it. If an
  // illness caused the death, staff raise that row to grade 5 and delete this.
  const [deathDraftAdded, setDeathDraftAdded] = useState(false);
  useEffect(() => {
    const eid = formData.enrollment_id;
    if (!eid || !aeLoaded || deathDraftAdded) return;
    api.get(`/enrollment/${encodeURIComponent(eid)}/death-status`)
      .then((res) => {
        if (!res.data?.needs_ae) return;
        setDeathDraftAdded(true);
        setIsSaved(false);
        setFormData((p) => ({
          ...p,
          has_adverse_event: "Yes",
          events: [...(p.events || []).filter((e) => e.description || e.definition_no),
            { ...blankEvent(), description: "Death", grade: "5", start_date: res.data.date || "" }],
        }));
      })
      .catch(() => {});
  }, [formData.enrollment_id, aeLoaded, deathDraftAdded]);

  const setHasAE = (v) => {
    setIsSaved(false);
    setFormData((p) => ({
      ...p,
      has_adverse_event: v,
      events: v === "Yes" ? (p.events?.length ? p.events : [blankEvent()]) : [],
    }));
  };

  const updateRow = (index, field, value) => {
    setIsSaved(false);
    setJustAddedRows((s) => {
      if (!s.has(index)) return s;
      const next = new Set(s);
      next.delete(index);
      return next;
    });
    setFormData((p) => {
      const events = [...(p.events || [])];
      const row = events[index];
      events[index] = { ...row, [field]: value };
      // The Grade box follows the text while it is blank or was filled from
      // the text; a grade picked by hand is kept.
      if (field === "grade") events[index].grade_auto = false;
      if (field === "severity_desc" && (!row.grade || row.grade_auto)) {
        events[index].grade = gradeFromText(value);
        events[index].grade_auto = !!gradeFromText(value);
      }
      return { ...p, events };
    });
  };

  const addRow = () => {
    setIsSaved(false);
    setFormData((p) => ({ ...p, events: [...(p.events || []), blankEvent()] }));
  };

  const removeRow = (index) => {
    setIsSaved(false);
    setJustAddedRows((s) => {
      if (!s.size) return s;
      const next = new Set();
      s.forEach((i) => {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      });
      return next;
    });
    setFormData((p) => ({
      ...p,
      events: (p.events || []).filter((_, i) => i !== index),
    }));
  };

  const scanForCandidates = async () => {
    if (!formData.enrollment_id) return;
    setScanning(true);
    setScanMessage("");
    try {
      // POST with the rows on screen: the server marks a candidate the form
      // already records (same term by key / code / name, start within 3 days)
      // as duplicate_of = that row's number (PI 2026-10-26).
      const res = await api.post(
        `/adverse-events/candidates/${encodeURIComponent(formData.enrollment_id)}`,
        { events: formData.has_adverse_event === "Yes" ? (formData.events || []) : [] },
      );
      const all = Array.isArray(res.data?.candidates) ? res.data.candidates : [];
      const dupes = all.filter((c) => c.duplicate_of);
      const candidates = all.filter((c) => !c.duplicate_of);
      const dupeNote = dupes.length
        ? ` ${dupes.length} already on the list: ${dupes.map((c) => `${c.description} (row ${c.duplicate_of})`).join(", ")}.`
        : "";
      setPendingCranialUsgFindings(
        Array.isArray(res.data?.pending_cranial_usg_findings)
          ? res.data.pending_cranial_usg_findings
          : [],
      );
      if (!candidates.length) {
        setScanMessage(
          dupes.length
            ? `Nothing new: every detected event is already on the list.${dupeNote}`
            : res.data?.has_data === false
              ? "No daily logs or Form H data found for this baby yet."
              : "No adverse-event candidates found in this baby's daily logs or Form H.",
        );
        setScanning(false);
        return;
      }
      setFormData((p) => {
        const existing = p.events || [];
        const already = new Set(
          existing.map((e) => `${e.definition_no}|${e.start_date}`),
        );
        const toAdd = candidates
          .filter((c) => !already.has(`${c.definition_no}|${c.start_date || ""}`))
          .map((c) => ({
            description: c.description || "",
            definition_no: c.definition_no || "",
            start_date: c.start_date || "",
            end_date: c.end_date || "",
            severity_desc: c.evidence
              ? `${c.severity_desc}\n\nEvidence (auto-detected from daily logs): ${c.evidence}`
              : c.severity_desc || "",
            grade: c.grade || "",
            converted_to_sae: "",
          }));
        if (!toAdd.length) {
          setScanMessage(`All detected candidates are already in the list below.${dupeNote}`);
          return p;
        }
        const startIdx = existing.length;
        setJustAddedRows((s) => {
          const next = new Set(s);
          toAdd.forEach((_, i) => next.add(startIdx + i));
          return next;
        });
        setScanMessage(
          `Added ${toAdd.length} candidate event${toAdd.length > 1 ? "s" : ""} — review and edit before saving.${dupeNote}`,
        );
        return { ...p, has_adverse_event: "Yes", events: [...existing, ...toAdd] };
      });
    } catch (err) {
      console.error(err?.response?.data || err);
      setScanMessage("Scan failed — please try again.");
    } finally {
      setScanning(false);
      setTimeout(() => setScanMessage(""), 6000);
    }
  };

  useEffect(() => {
    const id =
      routeId ||
      patientData?.enrollment_id ||
      location.state?.enrollmentId ||
      localStorage.getItem("current_enrollment_id") ||
      "";
    if (!id) return;

    setFormData((p) => ({ ...p, enrollment_id: id }));

    api
      .get(`/birth-resuscitation/${id}`)
      .then(async (res) => {
        const b = Array.isArray(res.data) ? res.data[0] : res.data;
        if (!b) return;
        let resolvedSite = b.site_name || patientData?.site_name || patientData?.site || "";
        let mother = "";
        let maternalUid = "";
        let babyUid = b.baby_uid || b.baby_annual_no || "";
        if (b.screening_id) {
          try {
            const screening = (await api.get(`/screenings/by-screening-id/${b.screening_id}`)).data;
            if (screening?.site_name) resolvedSite = screening.site_name;
          } catch {
            /* optional */
          }
          try {
            const pii = (await api.get(`/pii/screening/${b.screening_id}`)).data || {};
            mother = `${pii.mother_first_name || pii.mother_name_first || ""} ${pii.mother_surname || pii.mother_name_surname || ""}`.trim();
            maternalUid = pii.maternal_uid || "";
          } catch {
            /* optional */
          }
        }
        if (!mother) {
          mother = `${b.mother_name_first || ""} ${b.mother_name_surname || ""}`.trim();
        }
        if (resolvedSite) setSiteName(resolvedSite);
        setFormData((p) => ({
          ...p,
          enrollment_id: id,
          mother_name: p.mother_name || mother || patientData?.mother_name || "",
          maternal_uid: p.maternal_uid || maternalUid || patientData?.maternal_uid || "",
          baby_uid: p.baby_uid || babyUid || patientData?.baby_uid || "",
        }));
      })
      .catch(() => {});

    try {
      api.get(`/pii/enrollment/${id}`).then((res) => {
        const pii = res.data || {};
        const mother = `${pii.mother_first_name || ""} ${pii.mother_surname || ""}`.trim();
        setFormData((p) => ({
          ...p,
          mother_name: p.mother_name || mother || "",
          maternal_uid: p.maternal_uid || pii.maternal_uid || "",
        }));
      }).catch(() => {});
    } catch {
      /* optional */
    }

    api
      .get(`/adverse-events/${encodeURIComponent(id)}`)
      .then((res) => {
        setAeLoaded(true);
        // Backend returns null when no AE form has been saved yet
        if (!res.data || !res.data.enrollment_id) { baseEventsRef.current = []; return; }
        baseEventsRef.current = res.data.events || [];
        const mapped = mapApiToForm(res.data);
        syncTick(mapped);
        setFormData((p) => ({
          ...mapped,
          enrollment_id: id,
          mother_name: mapped.mother_name || p.mother_name,
          maternal_uid: mapped.maternal_uid || p.maternal_uid,
          baby_uid: mapped.baby_uid || p.baby_uid,
        }));
        setIsSaved(true);
      })
      .catch((err) => {
        // 404 = never saved (older servers); treat as empty form
        if (err?.response?.status === 404) { setAeLoaded(true); return; }
        console.error("Failed to load AE form", err);
      });
  }, [routeId, patientData, location.state]);

  useEffect(() => {
    const site = siteName || patientData?.site_name || patientData?.site || "";
    if (!site) {
      setAssessors([]);
      return;
    }
    api
      .get(`/sites/${encodeURIComponent(site)}/screeners`)
      .then((r) => setAssessors(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAssessors([]));
  }, [siteName, patientData?.site_name, patientData?.site]);

  const saveForm = async () => {
    if (!formData.enrollment_id) {
      setSaveMessage("❌ Enrollment ID is required");
      return false;
    }
    try {
      const res = await api.post("/adverse-events/", buildPayload(formData, baseEventsRef.current));
      baseEventsRef.current = res.data?.events || [];
      const mapped = mapApiToForm(res.data);
      setFormData((p) => ({
        ...mapped,
        mother_name: mapped.mother_name || p.mother_name,
        maternal_uid: mapped.maternal_uid || p.maternal_uid,
        baby_uid: mapped.baby_uid || p.baby_uid,
      }));
      syncTick(mapped);
      setIsSaved(true);
      setSaveMessage("✅ Adverse Events form saved");
      setTimeout(() => setSaveMessage(""), 3000);
      return true;
    } catch (err) {
      console.error(err?.response?.data || err);
      const detail = err?.response?.data?.detail;
      setSaveMessage(
        `❌ Save failed${detail ? `: ${typeof detail === "string" ? detail : JSON.stringify(detail)}` : ""}`,
      );
      setTimeout(() => setSaveMessage(""), 5000);
      return false;
    }
  };

  const showEvents = formData.has_adverse_event === "Yes";

  return (
    <form
      className="screening-form form-ae-page"
      onSubmit={async (e) => {
        e.preventDefault();
        const ok = await saveForm();
        if (ok) {
          navigate(`/sae-list/${formData.enrollment_id}`, {
            state: { enrollmentId: formData.enrollment_id },
          });
        }
      }}
    >
      <div className="form-header-action-row">
        <div className="form-header-title-area">
          <div className="form-breadcrumb">
            <Home size={12} /> HELPER · ADVERSE EVENTS
          </div>
          <h1 className="form-main-title">Adverse Events</h1>
          <p className="form-main-subtitle">
            Report any adverse event as per INC Adverse Event scale v1.0
          </p>
          <a
            className="ae-scale-btn"
            href={AE_SCALE_PDF}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FileText size={15} />
            Open INC AE Scale
          </a>
        </div>
        <div className="form-header-meta-area">
          <div className="screening-id-badge">
            <span className="id-label">Enrollment ID</span>
            <span className="id-val">{formData.enrollment_id || "—"}</span>
          </div>
        </div>
      </div>

      <SectionCard icon={ClipboardList} title="Identification">
        <div className="ae-grid-4">
          <div className="form-group">
            <label>Enrollment ID</label>
            <input className="ae-input" value={formData.enrollment_id} readOnly />
          </div>
          <div className="form-group">
            <label>Mother&apos;s name</label>
            <input
              className="ae-input"
              value={formData.mother_name}
              onChange={(e) => set("mother_name", stripDigits(e.target.value))}
            />
          </div>
          <div className="form-group">
            <label>Baby&apos;s UID</label>
            <input
              className="ae-input"
              value={formData.baby_uid}
              onChange={(e) => set("baby_uid", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Maternal UID</label>
            <input
              className="ae-input"
              value={formData.maternal_uid}
              onChange={(e) => set("maternal_uid", sanitizeMaternalUid(siteName || patientData?.site_name || patientData?.site, e.target.value))}
            />
          </div>
        </div>
      </SectionCard>

      <SectionCard icon={AlertCircle} title="Adverse event reported">
        <p className="ae-note" style={{ marginTop: 0 }}>
          Was any adverse event reported? If Yes, fill the details below.
        </p>
        <div className="ae-qa">
          <span className="ae-q">Any adverse event reported?</span>
          <YesNo value={formData.has_adverse_event} onChange={setHasAE} />
        </div>
        <div className="ae-scan-row">
          <button
            type="button"
            className="ae-scan-btn"
            onClick={scanForCandidates}
            disabled={scanning || !formData.enrollment_id}
          >
            <ScanSearch size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
            {scanning ? "Scanning…" : "Scan daily logs for AE candidates"}
          </button>
          <span className="ae-scan-hint">
            Checks metabolic, electrolyte, thermal, and AKI daily-log data (more domains coming).
            Suggestions only — review and edit every field before saving.
          </span>
        </div>
        {scanMessage && <p className="ae-scan-msg">{scanMessage}</p>}
        {pendingCranialUsgFindings.length > 0 && (
          <div className="field-hint field-hint-warning" style={{ marginTop: 10 }}>
            ⚠ Form F (Cranial USG) has a severe finding not yet reflected in Form H:
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {pendingCranialUsgFindings.map((f, i) => (
                <li key={i}>{f.message}</li>
              ))}
            </ul>
            This is not added as an AE candidate automatically — please complete/update
            Form H for this baby first, then re-scan.
          </div>
        )}
      </SectionCard>

      {showEvents && (
        <SectionCard icon={ListChecks} title="List of adverse events">
          <div className="ae-table-wrap">
            <table className="ae-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Description of adverse event</th>
                  <th>Definition number</th>
                  <th>Start date</th>
                  <th>End date</th>
                  <th>Severity grade (full description)</th>
                  <th>Grade (1–5)</th>
                  <th>Converted to SAE</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(formData.events || []).length === 0 ? (
                  <tr>
                    <td colSpan={9}>
                      <div className="ae-empty">No events yet — click Add event</div>
                    </td>
                  </tr>
                ) : (
                  formData.events.map((row, idx) => (
                    <tr key={idx} className={justAddedRows.has(idx) ? "ae-row-auto" : undefined}>
                      <td className="ae-num">{idx + 1}</td>
                      <td className="ae-desc-col">
                        {justAddedRows.has(idx) && (
                          <span className="ae-auto-badge" title="Suggested from a daily-log scan — review before saving">
                            auto-detected
                          </span>
                        )}
                        <ExpandableText
                          value={row.description}
                          onChange={(v) => updateRow(idx, "description", v)}
                          placeholder="Event description"
                          title={`Event #${idx + 1} — Description`}
                        />
                      </td>
                      <td style={{ minWidth: 90 }}>
                        <input
                          value={row.definition_no}
                          onChange={(e) => updateRow(idx, "definition_no", e.target.value)}
                          placeholder="No."
                        />
                      </td>
                      <td style={{ minWidth: 130 }}>
                        <DateField
                          value={row.start_date}
                          onChange={(v) => updateRow(idx, "start_date", v)}
                        />
                      </td>
                      <td style={{ minWidth: 130 }}>
                        <DateField
                          value={row.end_date}
                          onChange={(v) => updateRow(idx, "end_date", v)}
                        />
                      </td>
                      <td className="ae-desc-col">
                        <ExpandableText
                          value={row.severity_desc}
                          onChange={(v) => updateRow(idx, "severity_desc", v)}
                          placeholder="Full severity description"
                          title={`Event #${idx + 1} — Severity description`}
                        />
                      </td>
                      <td style={{ minWidth: 80 }}>
                        <select
                          value={row.grade}
                          onChange={(e) => updateRow(idx, "grade", e.target.value)}
                        >
                          <option value="">—</option>
                          {GRADES.map((g) => (
                            <option key={g} value={g}>
                              {g}
                            </option>
                          ))}
                        </select>
                        {gradeFromText(row.severity_desc) && row.grade
                          && row.grade !== gradeFromText(row.severity_desc) && (
                          <div className="ae-grade-mismatch">
                            Text says Grade {gradeFromText(row.severity_desc)}
                          </div>
                        )}
                      </td>
                      <td>
                        <YesNo
                          small
                          value={row.converted_to_sae}
                          onChange={(v) => updateRow(idx, "converted_to_sae", v)}
                        />
                        {saeLinkedTo(row) && (
                          <div className="ae-sae-link">
                            Linked to SAE report
                            {saeLinkedTo(row).report_date ? ` (${dateOnly(saeLinkedTo(row).report_date)})` : ""}
                            {row.converted_to_sae !== "Yes" && (
                              <>
                                {" — "}
                                <button type="button" className="ae-sae-apply"
                                  onClick={() => updateRow(idx, "converted_to_sae", "Yes")}>Apply: Yes</button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="ae-del"
                          title="Remove row"
                          onClick={() => removeRow(idx)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="ae-toolbar">
            <button type="button" className="ae-add" onClick={addRow}>
              <Plus size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
              Add event
            </button>
          </div>
        </SectionCard>
      )}

      <SectionCard icon={UserRound} title="Form completed by">
        <div className="ae-grid-3">
          <div className="form-group">
            <label>Name of person filling this form</label>
            <select
              className="ae-input"
              value={formData.completed_by || ""}
              onChange={(e) => {
                const name = e.target.value;
                setIsSaved(false);
                setFormData((p) => ({
                  ...p,
                  completed_by: name,
                  designation: getDesignation(name) || p.designation,
                }));
              }}
            >
              <option value="">Select…</option>
              {assessors.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
              {formData.completed_by && !assessors.includes(formData.completed_by) && (
                <option value={formData.completed_by}>{formData.completed_by}</option>
              )}
            </select>
          </div>
          <div className="form-group">
            <label>Designation</label>
            <input
              className="ae-input"
              value={formData.designation}
              onChange={(e) => set("designation", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Date</label>
            <DateField
              value={formData.completion_date}
              onChange={(v) => set("completion_date", v)}
            />
          </div>
        </div>
      </SectionCard>

      {saveMessage && (
        <p className={`ae-save-msg${saveMessage.startsWith("❌") ? " err" : ""}`}>
          {saveMessage}
        </p>
      )}

      <FormNavBar
        backLabel="Form Y"
        nextLabel="SAE List"
        step={2}
        totalSteps={3}
        isSaved={isSaved}
        onBack={() =>
          navigate(`/form-y-sae/${formData.enrollment_id || ""}`, {
            state: { enrollmentId: formData.enrollment_id },
          })
        }
        onSave={saveForm}
        onNext={async () => {
          if (!isSaved) {
            const ok = await saveForm();
            if (!ok) return;
          }
          navigate(`/sae-list/${formData.enrollment_id}`, {
            state: { enrollmentId: formData.enrollment_id },
          });
        }}
      />
    </form>
  );
}
